import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Chip, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import GlobalDialogContent from '../GlobalDialogContent';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { closeDialog } from '../../../redux/reducer/dialogSlice';
import { StoreFundUtilized } from '../../../utils/services/project/project';
import { setModuleRefresh } from '../../../redux/reducer/registration/RefreshSlice';
import { AmountDisplay } from '../../../utils/utils';
import PageLang from '../../../config/page.languge';


const AddFundUtilizationDialog = () => {
    const { payload } = useSelector((state: RootState) => state.dialog);
    const dispatch = useDispatch();
    const RemainingAmount: number = Number(payload?.data.fund_disbursed) - Number(payload?.data.fund_utilize);
    const lang=useSelector((state:RootState)=>state.lang.language)
    const formik = useFormik({
        initialValues: {
            project_name: payload.project_name,
            agency_name: payload.agency_name,
            date: dayjs(),
            amount: RemainingAmount,
            remark: '',
            attachment: null as File | null,
            utilizationType: 'Expense',
        },
        validationSchema: Yup.object().shape({
            date: Yup.date().required(PageLang?.fundDisbUtilise[14][lang]),
            amount: Yup.number().required(PageLang?.fundDisbUtilise[15][lang]).positive(PageLang?.fundDisbUtilise[16][lang])
            .max(RemainingAmount, `${PageLang?.fundDisbUtilise[17][lang]} ${RemainingAmount}`),
            remark: Yup.string(),
            utilizationType: Yup.string().required(PageLang?.fundDisbUtilise[18][lang] ),
            attachment: Yup.mixed<File>().nullable()
            .test("fileSize", PageLang?.forms.fileSizeLimit[lang], (file: any) => { return file && file.size <= 5 * 1024 * 1024; })
            .test("fileType", "Only PDF, JPG, and PNG files are allowed", (file: any) => { return (file && ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(file.type)); }),
        }),
        onSubmit: async (values) => {
            const formData = new FormData();
            formData.append("project_id", payload?.project_id);
            formData.append("agency_id", payload?.agency_id);
            formData.append("utilization_date", dayjs(values.date).format("YYYY-MM-DD"));
            formData.append("utilized_amount", String(values.amount));
            formData.append("utilization_type", values.utilizationType);
            formData.append("remarks", values.remark);
            formData.append("disbursement_id", payload.distribution_id);
            formData.append("documents", values.attachment || '');

            const { code, message } = await StoreFundUtilized(formData);

            if (code === 200) {
                dispatch(closeDialog());
                dispatch(setModuleRefresh({ moduleName: 'FundUtilization', refresh: true }));
                dispatch(showSnackbar({ type: "success", message: message || "Utilization recorded successfully!" }));
            } else {
                dispatch(showSnackbar({ type: "error", message: message || "Failed to record utilization" }));
            }
        },
    });

    const handleFileChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;
    
        const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
        const maxSize = 5 * 1024 * 1024;
    
        if (!allowedTypes.includes(file.type)) {
            dispatch(showSnackbar({ type: 'error', message: 'Only PDF, JPG, and PNG files are allowed.' }));
            return;
        }
    
        if (file.size > maxSize) {
            dispatch(showSnackbar({ type: 'error', message: PageLang?.forms.fileSizeLimit[lang] }));
            return;
        }
    
        formik.setFieldValue(fieldName, file);
    };

    return (
        <Box component="div" sx={{ bgcolor: 'background.default' }}>
            <GlobalDialogContent
                dialogBody={
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={PageLang?.fundDisbUtilise[3][lang]}
                                        value={formik.values.project_name}
                                        size="small"
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={PageLang?.fundDisbUtilise[4][lang]}
                                        value={formik.values.agency_name}
                                        size="small"
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        value={formik.values.date ? dayjs(formik.values.date) : null}
                                        onChange={(value) =>
                                            formik.setFieldValue("date", value ? value.toDate() : null)
                                        }
                                        sx={{ ".MuiButtonBase-root": { marginRight: 0 } }}
                                        format="DD-MM-YYYY"
                                        maxDate={dayjs()}
                                        slotProps={{
                                            textField: {
                                                label:PageLang?.fundDisbUtilise[5][lang],
                                                fullWidth: true,
                                                size: "small",
                                                error: formik.touched.date && Boolean(formik.errors.date),
                                                helperText: formik.touched.date && Boolean(formik.errors.date),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={PageLang?.fundDisbUtilise[6][lang]}
                                        name="amount"
                                        value={AmountDisplay(Number(formik.values.amount))}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9.]/g, '');
                                            const digitCount = value.replace('.', '').length;
                                            if (digitCount <= 10) {
                                                formik.setFieldValue('amount', value);
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.amount && Boolean(formik.errors.amount)}
                                        helperText={formik.touched.amount && formik.errors.amount}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment position="end" sx={{ mr: 2 }}>₹</InputAdornment>
                                        }}
                                        inputProps={{ style: { textAlign: "right" } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <FormControl
                                        component="fieldset"
                                        error={formik.touched.utilizationType && Boolean(formik.errors.utilizationType)}
                                    >
                                        <FormLabel component="legend">{PageLang?.fundDisbUtilise[7][lang]}</FormLabel>
                                        <RadioGroup
                                            row
                                            name="utilizationType"
                                            value={formik.values.utilizationType}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        >
                                            {["Expense", "Asset Purchase", "Salary", "Other"].map((type) => (
                                                <FormControlLabel
                                                    key={type}
                                                    value={type}
                                                    control={<Radio />}
                                                    label={type}
                                                />
                                            ))}
                                        </RadioGroup>
                                        <FormHelperText>{formik.touched.utilizationType && formik.errors.utilizationType}</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="remark"
                                        value={formik.values.remark}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.remark && Boolean(formik.errors.remark)}
                                        helperText={formik.touched.remark && formik.errors.remark}
                                        placeholder={PageLang?.fundDisbUtilise[19][lang] }
                                        multiline
                                        rows={3}
                                        size="small"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box>
                                        <input
                                            accept=".pdf,jpg,.png"
                                            id="upload-utilization"
                                            type="file"
                                            style={{ display: 'none' }}
                                            onChange={(event) =>
                                                // formik.setFieldValue('attachment', event.currentTarget.files?.[0] || null)
                                                handleFileChange(event , 'attachment')
                                            }
                                        />
                                        {!formik.values.attachment && (
                                            <label htmlFor="upload-utilization">
                                                <Button variant="outlined" sx={{ p: 1, mb: 2 }} component="span" size="small" fullWidth>
                                                {PageLang?.fundDisbUtilise[20][lang] }
                                                </Button>
                                            </label>
                                        )}
                                        {formik.values.attachment && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Chip
                                                    label={formik.values.attachment.name}
                                                    sx={{ maxWidth: 200 }}
                                                    color="primary"
                                                    variant="outlined"
                                                    onDelete={() => formik.setFieldValue('attachment', null)}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </FormikProvider>
                }
                dialogFooter={
                    <Button onClick={() => formik.handleSubmit()} type="submit" variant="contained" color="success" endIcon={<CheckCircleOutline />}>
                        {PageLang?.fundDisbUtilise[9][lang] }
                    </Button>
                }
            />
        </Box>
    );
};

export default AddFundUtilizationDialog;
